import {
  ExtranetCreateTicketPostDto,
  ExtranetTicketSubjectAreaDto,
} from '@pharmac-extranet/extranet-api'
import {
  ControlledInput,
  ControlledSelect,
  ExtranetContainer,
  ExtranetModal,
  ExtranetModalBody,
  ExtranetModalHeader,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacButton,
  PharmacH4,
  PharmacH6,
  PharmacIcon,
  PharmacLoadingDots,
  PharmacText,
  PharmacTooltip,
} from '@pharmac/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { Control, FieldValues, FormProvider, useForm } from 'react-hook-form'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { api } from '../../services/HttpService'
import { NewTicketAttachment } from './TicketAttachments'

type NewTicketModalProps = {
  closeModal: () => void
}

type NewTicketAttachments = { [uuid: string]: File }

export const NewTicketModal = ({ closeModal }: NewTicketModalProps) => {
  const { t, key } = useLanguage()
  const { minDesktop, maxMobile } = useBreakpoint()
  const { customer } = useCustomerContext()
  const queryClient = useQueryClient()

  const [subjectAreas, setSubjectAreas] = useState<ExtranetTicketSubjectAreaDto[]>([])

  const [attachments, setAttachments] = useState<NewTicketAttachments>({})

  const fileUploadRef = useRef<HTMLInputElement | null>(null)

  const { data: subjectAreasData, isLoading: subjectAreasLoading } = useQuery({
    queryKey: ['__getTicketSubjectAreas'],
    queryFn: async () => await api.tickets.getSubjectAreas(Number(customer!.id)),
  })

  useEffect(() => {
    if (subjectAreasData && !subjectAreasLoading) {
      setSubjectAreas(subjectAreasData)
    }
  }, [subjectAreasData, subjectAreasLoading])

  const formhook = useForm<ExtranetCreateTicketPostDto>({
    mode: 'onChange',
    defaultValues: {
      isPrivate: false,
      isConfidential: false,
      title: '',
      description: '',
      subjectArea: 'none',
    },
  })

  const subjectArea = formhook.watch('subjectArea')
  const isPrivate = formhook.watch('isPrivate')
  const isConfidential = formhook.watch('isConfidential')

  const [isMutating, setIsMutating] = useState(false)

  const { mutate: createTicket } = useMutation({
    mutationFn: async (dto: ExtranetCreateTicketPostDto) => {
      setIsMutating(true)
      return await api.tickets.createTicket(Number(customer?.id), dto, {
        transformRequest: [
          (data) => {
            if (data instanceof FormData) {
              // The API client produces malformed data for files so we replace it
              if (data.has('files')) {
                data.delete('files')
              }

              for (const file of (dto.files || []) as File[]) {
                data.append('files[]', file as Blob, encodeURIComponent(file.name))
              }
            }

            return data
          },
        ],
      })
    },
    onSettled: () => setIsMutating(false),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['__getTickets', customer!.id],
      })

      closeModal()
    },
    onError: (error) => {
      console.log(format(new Date(), 'hh:mm:ss'), 'NewTicketModal:: DEBUG ::', {
        error,
      })

      closeModal()
    },
  })

  const createNewTicket = (data: ExtranetCreateTicketPostDto) => {
    const ticketAttachments = Object.values(attachments)

    if (ticketAttachments.length > 0) {
      data.files = Object.values(ticketAttachments)
    }

    createTicket(data)
  }

  const handleFileInputClick = () => {
    if (fileUploadRef.current) {
      fileUploadRef.current.click()
    }
  }

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const t = event.currentTarget
    if (!t.files) return

    const newFiles: NewTicketAttachments = {}

    for (const file of t.files) {
      newFiles[crypto.randomUUID()] = file
    }

    setAttachments((s) => ({ ...s, ...newFiles }))
  }

  const handleRemoveAttachment = (id: string) => {
    if (Object.keys(attachments).includes(id)) {
      setAttachments((s) => {
        const copy = { ...s }
        delete copy[id]

        return copy
      })
    }
  }

  const checkIsValid = () => {
    if (!formhook.formState.isValid) return false

    const subjectAreaIsValid = !!subjectArea && subjectArea !== 'none'

    if (!subjectAreaIsValid) return false

    return true
  }

  return (
    <ExtranetModal
      modalBackground={'#5A285033'}
      closeOnOutsideClick={false}
      preventClose
      style={{ backgroundColor: 'var(--color-accent-2)', width: minDesktop ? '40vw' : undefined }}
    >
      <ExtranetModalHeader>
        <ExtranetContainer
          full
          flexRow
          justifyCenter
          paddingMediumVertical
        >
          <PharmacH4 color="purple">{t('extranet:contact.newTicketModalTitle')}</PharmacH4>
        </ExtranetContainer>
      </ExtranetModalHeader>
      <ExtranetModalBody>
        <FormProvider {...formhook}>
          <ExtranetContainer
            full
            flexColumn
            paddingLargeHorizontal={minDesktop}
            paddingSmallHorizontal={!minDesktop}
          >
            <ExtranetContainer
              full
              alignCenter
              paddingLargeBottom
              flexRow={!maxMobile}
              flexColumn={maxMobile}
            >
              <ExtranetContainer
                half={!maxMobile}
                full={maxMobile}
                justifyStart={!maxMobile}
                justifyCenter={maxMobile}
                alignCenter
                marginMediumBottom={maxMobile}
              >
                <PharmacButton
                  marginSmallRight
                  variant="secondary"
                  onClick={() => formhook.setValue('isPrivate', !isPrivate)}
                  style={{
                    backgroundColor: isPrivate ? 'var(--color-primary)' : 'var(--color-white)',
                    color: isPrivate ? 'var(--color-white)' : 'var(--color-black)',
                  }}
                >
                  <PharmacIcon
                    style={{ marginRight: 'var(--spacing-small)' }}
                    size="20"
                    icon="lock"
                  />
                  {t('contact.privateTicketButton')}
                </PharmacButton>
                <PharmacTooltip
                  position="top-end"
                  handle={
                    <PharmacIcon
                      icon="info"
                      style={{
                        color: 'var(--color-green)',
                        marginRight: 'var(--spacing-tiny)',
                        marginTop: 'var(--spacing-tiny)',
                      }}
                    />
                  }
                >
                  {t('contact.privateTicketInfo')}
                </PharmacTooltip>
              </ExtranetContainer>
              <ExtranetContainer
                half={!maxMobile}
                full={maxMobile}
                justifyStart={!maxMobile}
                justifyCenter={maxMobile}
                alignCenter
              >
                <PharmacButton
                  variant="secondary"
                  onClick={() => formhook.setValue('isConfidential', !isConfidential)}
                  marginSmallRight
                  style={{
                    backgroundColor: isConfidential ? 'var(--color-primary)' : 'var(--color-white)',
                    color: isConfidential ? 'var(--color-white)' : 'var(--color-black)',
                  }}
                >
                  <PharmacIcon
                    style={{ marginRight: 'var(--spacing-small)' }}
                    size="20"
                    icon="eyeOff"
                  />
                  {t('contact.confidentialTicketButton')}
                </PharmacButton>

                <PharmacTooltip
                  position="top-end"
                  handle={
                    <PharmacIcon
                      icon="info"
                      style={{
                        color: 'var(--color-green)',
                        marginRight: 'var(--spacing-tiny)',
                        marginTop: 'var(--spacing-tiny)',
                      }}
                    />
                  }
                >
                  {t('contact.confidentialTicketInfo')}
                </PharmacTooltip>
              </ExtranetContainer>
            </ExtranetContainer>
            <ExtranetContainer
              full
              flexColumn
              paddingLargeBottom
            >
              <ExtranetContainer
                full
                justifyStart
                paddingSmallBottom
              >
                <PharmacH6>{t('extranet:contact.subjectAreaLabel')}*</PharmacH6>
              </ExtranetContainer>
              <ExtranetContainer
                full
                flexRow
              >
                <ControlledSelect
                  control={formhook.control as unknown as Control<FieldValues>}
                  name="subjectArea"
                  rules={{ required: true }}
                  requiredError={t('common:requiredFieldError')}
                >
                  <option
                    key="no-value"
                    value="none"
                  ></option>
                  {subjectAreas.map((a) => (
                    <option
                      key={a.id}
                      value={a.id}
                    >
                      {key(a.name)}
                    </option>
                  ))}
                </ControlledSelect>
              </ExtranetContainer>
            </ExtranetContainer>
            <ExtranetContainer
              full
              flexColumn
              paddingLargeBottom
            >
              <ExtranetContainer
                full
                flexRow
                paddingSmallBottom
              >
                <ExtranetContainer
                  half
                  justifyStart
                >
                  <PharmacH6>{t('extranet:contact.subject')}*</PharmacH6>
                </ExtranetContainer>
                <ExtranetContainer
                  half
                  justifyEnd
                >
                  <PharmacText>{formhook.watch('title').length}/450</PharmacText>
                </ExtranetContainer>
              </ExtranetContainer>
              <div style={{ width: '100%' }}>
                <ControlledInput
                  name="title"
                  rules={{ required: true, maxLength: 450 }}
                  requiredError={t('common:requiredFieldError')}
                  control={formhook.control as unknown as Control<FieldValues>}
                />
              </div>
            </ExtranetContainer>
            <ExtranetContainer
              full
              flexColumn
              paddingSmallBottom
            >
              <ExtranetContainer
                full
                flexRow
                paddingSmallBottom
              >
                <ExtranetContainer
                  half
                  justifyStart
                >
                  <PharmacH6>{t('extranet:contact.description')}*</PharmacH6>
                </ExtranetContainer>
                <ExtranetContainer
                  half
                  justifyEnd
                >
                  <PharmacText>{formhook.watch('description').length}/3500</PharmacText>
                </ExtranetContainer>
              </ExtranetContainer>
              <div style={{ width: '100%' }}>
                <ControlledInput
                  type="textarea"
                  name="description"
                  rules={{ required: true, maxLength: 3500 }}
                  requiredError={t('common:requiredFieldError')}
                  control={formhook.control as unknown as Control<FieldValues>}
                />
              </div>
            </ExtranetContainer>
            <ExtranetContainer
              paddingMediumTop
              alignCenter
            >
              <input
                ref={fileUploadRef}
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.webp"
                onChange={handleFileSelect}
                hidden
              />
              <PharmacButton
                size="small"
                marginMediumBottom={!minDesktop}
                variant="secondary"
                onClick={() => handleFileInputClick()}
              >
                <PharmacIcon icon="paperclip" />
                <PharmacText
                  marginSmallLeft
                  weight="semibold"
                >
                  {t('extranet:contact.addAttachment')}
                </PharmacText>
              </PharmacButton>
              {minDesktop && (
                <PharmacText paddingMediumLeft>
                  {t('extranet:contact.acceptableFileTypes')}
                </PharmacText>
              )}
            </ExtranetContainer>
            {!minDesktop && <PharmacText> {t('extranet:contact.acceptableFileTypes')}</PharmacText>}
            {Object.keys(attachments).length > 0 && (
              <ExtranetContainer>
                {Object.entries(attachments).map(([id, file]) => (
                  <NewTicketAttachment
                    attachment={file}
                    key={id}
                    removeAttachment={() => handleRemoveAttachment(id)}
                  />
                ))}
              </ExtranetContainer>
            )}
            <ExtranetContainer
              marginLargeVertical
              justifyCenter
              full
              style={{
                borderTop: '2px solid var(--color-accent-4)',
                borderBottom: '2px solid var(--color-accent-4)',
              }}
            >
              <PharmacText paddingLargeVertical>
                {t('extranet:contact.newTicketModalInfo')}
              </PharmacText>
            </ExtranetContainer>
            <ExtranetContainer
              full
              justifyEnd
              paddingLargeTop
            >
              {isMutating && (
                <ExtranetContainer style={{ marginRight: 'auto' }}>
                  <PharmacLoadingDots />
                </ExtranetContainer>
              )}
              <PharmacButton
                variant="secondary"
                onClick={() => {
                  closeModal()
                  formhook.reset()
                }}
              >
                {t('common:cancel')}
              </PharmacButton>
              <PharmacButton
                marginMediumLeft
                disabled={!checkIsValid() || isMutating}
                onClick={formhook.handleSubmit(createNewTicket)}
              >
                {t('extranet:contact.sendButton')}
              </PharmacButton>
            </ExtranetContainer>
          </ExtranetContainer>
        </FormProvider>
      </ExtranetModalBody>
    </ExtranetModal>
  )
}
